import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

/**
 * A horizontally-scrolling flex row with a hidden scrollbar. When content
 * overflows, a chevron button appears on that side (over a soft gradient fade)
 * to signal "there's more — scroll this way", and clicking it nudges the row
 * along. The chevron disappears once that end is reached, so it doubles as an
 * "at the end" cue. This is the affordance the bare hidden-scrollbar row lacked.
 *
 * `className`        → utilities for the inner flex row (gap, alignment, padding).
 * `wrapperClassName` → utilities for the outer positioned wrapper (e.g. margins).
 * `fadeClass`        → gradient start color; match the surface behind the row.
 */
const ScrollHintRow = ({
    children,
    className = '',
    wrapperClassName = '',
    fadeClass = 'from-white dark:from-gray-900',
}) => {
    const ref = useRef(null);
    const [edges, setEdges] = useState({ left: false, right: false });

    const update = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setEdges({
            left: scrollLeft > 1,
            right: Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1,
        });
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        update();
        el.addEventListener('scroll', update, { passive: true });
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', update);
            ro.disconnect();
        };
    }, [update]);

    const nudge = (dir) => {
        const el = ref.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' });
    };

    const Cue = ({ side, show }) => {
        const isLeft = side === 'left';
        return (
            <div
                className={`absolute inset-y-0 ${isLeft ? 'left-0' : 'right-0'} flex items-center transition-opacity duration-200 ${
                    show ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
            >
                {/* soft fade so pills dissolve into the bar behind the chevron */}
                <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-y-0 w-12 ${fadeClass} to-transparent ${
                        isLeft ? 'left-0 bg-gradient-to-r' : 'right-0 bg-gradient-to-l'
                    }`}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    aria-label={isLeft ? 'Scroll left' : 'Scroll right'}
                    onClick={() => nudge(isLeft ? -1 : 1)}
                    className={`tap-target relative h-9 w-9 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-200 shadow-e1 ring-1 ring-black/5 hover:text-brand-600 dark:hover:text-brand-400 hover:scale-105 transition ${
                        isLeft ? 'ml-0.5' : 'mr-0.5'
                    }`}
                >
                    <FontAwesomeIcon icon={isLeft ? faChevronLeft : faChevronRight} className="text-xs" />
                </button>
            </div>
        );
    };

    return (
        <div className={`relative min-w-0 ${wrapperClassName}`}>
            <div
                ref={ref}
                className={`flex overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}
            >
                {children}
            </div>
            <Cue side="left" show={edges.left} />
            <Cue side="right" show={edges.right} />
        </div>
    );
};

export default ScrollHintRow;
